import {FormProvider, useForm, UseFormProps} from 'react-hook-form';
import {
    FormHTMLAttributes, ForwardedRef,
    forwardRef,
    PropsWithChildren,
    useEffect,
    useImperativeHandle, useMemo,
    useRef,
    useState
} from 'react';
import axios, {AxiosError} from 'axios';
import {UseFormReturn} from 'react-hook-form/dist/types';
import {useAppSelector} from '@hooks/app';
import {noop} from '@utils/constants';
import {QueryMapRules, useMapUrlParams} from '@ui/Form/utils';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useAuthAxios} from '@hooks/useAuthAxios';
import qs from 'qs';
import moment from 'moment/moment';
import {useDebounce} from 'use-debounce';
import {devBaseUrl, prodBaseUrl} from '../../../api';

type FormProps = PropsWithChildren<UseFormProps & Omit<FormHTMLAttributes<any>, 'onSubmit' | 'onChange'> & {
    onInvalid?: (values: any) => any;
    onChange?: (values: any) => any;

    beforeSubmit?: (values: any) => any;
    afterSubmit?: (data: any) => any;
    onError?: (data: AxiosError<any>) => any;

    action?: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete',

    form?: UseFormReturn
    type?: 'json' | 'fd'

    storeInUrl?: boolean
    mapQueryParams?: QueryMapRules
    autoSubmit?: boolean
    restoreFromStorage?: () => Record<string, any> | null;
}>;

export const Form = ({
                         children,
                         onInvalid = noop,
                         afterSubmit = noop,
                         onError = noop,
                         beforeSubmit = v => v,
                         onChange = noop,

                         action: url,
                         method = 'post',

                         form: _form,
                         type = 'json',

                         storeInUrl = false,
                         mapQueryParams = {},
                         restoreFromStorage,

                         // useForm params
                         mode,
                         reValidateMode,
                         defaultValues,
                         resolver,
                         context,
                         shouldFocusError,
                         shouldUnregister,
                         shouldUseNativeValidation,
                         criteriaMode,
                         delayError,
                         autoSubmit = false,

                         ...formAttrs
                     }: FormProps) => {
    let form = useForm({
        mode,
        reValidateMode,
        defaultValues,
        resolver,
        context,
        shouldFocusError,
        shouldUnregister,
        shouldUseNativeValidation,
        criteriaMode,
        delayError,
    });
    const axios = useAuthAxios();
    const formRef = useRef<HTMLFormElement>();
    const mapParams = useMapUrlParams(mapQueryParams);
    const navigate = useNavigate();
    const timezone_offset = useMemo(() => (new Date()).getTimezoneOffset(), []);
    const [searchParams] = useSearchParams();

    if (_form) {
        form = _form;
    }

    const handleAJAXError = (error: AxiosError<any>) => {
        const {response} = error;
        if (!response) {
            return;
        } else if (response.status === 422 && response.data?.['errors']) {
            for (const [name, error] of Object.entries(response.data['errors'])) {
                form.setError(name, error, {shouldFocus: true});
            }
            return;
        } else if (response.status !== 422 && response.data?.['message']) {
            // @ts-ignore
            form.setError('_', [response.data['message']], {shouldFocus: true});
        } else {
            console.error(error);
        }

        onError(error);
    };

    const setUrlParams = () => {
        const payload = mapParams(form.watch());
        navigate({
            search: qs.stringify(payload, {
                serializeDate: d => moment(d)
                    .utc(true)
                    .subtract(timezone_offset, 'minutes')
                    .toISOString()
            })
        }, {
            relative: 'path'
        });
    };

    const fetchData = async () => {
        const value = mapParams(form.watch());

        try {
            let data = await beforeSubmit(value);
            if (data === undefined) {
                data = value;
            }

            if (type === 'fd') {
                data = new FormData(formRef.current);
            }

            if (url) {
                const res = await axios.request({
                    baseURL: process.env.NODE_ENV === 'development'
                        ? devBaseUrl
                        : prodBaseUrl,
                    headers: {
                        'Accept': 'application/json',
                    },
                    method,
                    url,
                    data,
                    params: ['get'].indexOf(method) >= 0 ? data : {}
                });
                afterSubmit(res.data);
            } else {
                afterSubmit(data);
            }
        } catch (e: any) {
            handleAJAXError(e);
        }
    };

    const handleSubmit = () => {
        // form.setValue('page', null);
        if (storeInUrl) {
            setUrlParams();
        } else {
            fetchData();
        }
    };

    useEffect(() => {
        if (!autoSubmit) {
            return;
        }
        if (storeInUrl) {
            const urlString = searchParams.toString();
            if (!urlString && restoreFromStorage) {
                const saved = restoreFromStorage();
                if (saved) {
                    form.reset(saved);
                    setUrlParams();
                    return;
                }
            }
            form.reset(mapParams(qs.parse(urlString)));
            setUrlParams();
        } else {
            fetchData();
        }
    }, [autoSubmit]);

    // useEffect(() => {
    //     if (storeInUrl && autoSubmit) {
    //         form.reset(mapParams(qs.parse(searchParams.toString())));
    //         fetchData();
    //     }
    // }, [searchParams.toString(), autoSubmit]);

    const [debouncedForm] = useDebounce(JSON.stringify(form.watch()), 500);

    useEffect(() => {
        if(!autoSubmit) {
            return;
        }

        fetchData();

    }, [debouncedForm]);

    return <FormProvider {...form}>
        <form
            ref={formRef}
            onSubmit={form.handleSubmit(handleSubmit, onInvalid)}
            onChange={() => onChange(mapParams(form.watch()))}
            {...formAttrs}
        >
            {children}
        </form>
    </FormProvider>;
};

